import { useLayoutEffect, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import Picker from '@emoji-mart/react';
import emojiData from '@emoji-mart/data';
import { useFloatingPosition } from '../../../../../hooks/useFloatingPosition';

const PICKER_ESTIMATED_HEIGHT = 360;
const VIEWPORT_MARGIN = 8;
const POPOVER_Z_INDEX = 9000; // below toasts (z-[9999]), same layer as QuickRepliesPopover

interface EmojiPickerPopoverProps {
    triggerRef: RefObject<HTMLElement>;
    popoverRef: RefObject<HTMLDivElement>;
    onSelect: (emoji: { native: string }) => void;
}

// The picker sizes itself after mount, so its width is only known once it has rendered.
function useRenderedWidth(ref: RefObject<HTMLElement>): number {
    const [width, setWidth] = useState(0);
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        const observer = new ResizeObserver(() => setWidth(el.offsetWidth));
        observer.observe(el);
        return () => observer.disconnect();
    }, [ref]);
    return width;
}

function clampLeft(left: number, width: number): number {
    const maxLeft = window.innerWidth - width - VIEWPORT_MARGIN;
    return Math.max(VIEWPORT_MARGIN, Math.min(left, maxLeft));
}

function EmojiPickerPopover({ triggerRef, popoverRef, onSelect }: EmojiPickerPopoverProps) {
    const position = useFloatingPosition({
        open: true,
        triggerRef,
        preferUp: true,
        estimatedHeight: PICKER_ESTIMATED_HEIGHT
    });
    const width = useRenderedWidth(popoverRef);

    return createPortal(
        <div
            ref={popoverRef}
            role="dialog"
            aria-label="Emoji picker"
            style={{
                position: 'fixed',
                zIndex: POPOVER_Z_INDEX,
                top: position?.top,
                bottom: position?.bottom,
                left: clampLeft(position?.left ?? 0, width),
                visibility: position && width > 0 ? 'visible' : 'hidden'
            }}
            className="shadow-xl rounded-xl overflow-hidden"
        >
            <Picker
                data={emojiData}
                onEmojiSelect={onSelect}
                theme="light"
                previewPosition="none"
                skinTonePosition="none"
                navPosition="bottom"
                perLine={8}
                emojiSize={20}
                maxFrequentRows={1}
            />
        </div>,
        document.body
    );
}

export { EmojiPickerPopover };
